import React from 'react';
import PropTypes from 'prop-types';
import styled, { withTheme } from 'styled-components';
import { compose } from 'recompose';

import { Box, Flex } from 'Components/Base';
import { withOffset } from 'Context/LayoutContext';

import { useClassroomContext, LAYOUT_TYPE } from '../../../../Context/ClassroomContext';

const getHeaderStyle = (layoutType) => {
  switch (layoutType) {
    case LAYOUT_TYPE.QUIZ:
      return {
        sticky: true,
        styles: {
          background: 'none',
        },
      };
    case LAYOUT_TYPE.DEFAULT_STICKY:
      return {
        sticky: true,
        styles: {
          backgroundImage: 'linear-gradient(to top, rgba(0, 0, 0, 0), #000000)',
        },
      };
    case LAYOUT_TYPE.EBOOK:
      return {
        sticky: true,
      };
    case LAYOUT_TYPE.DEFAULT:
    default:
      return {
        sticky: false,
        styles: {
          backgroundImage: 'linear-gradient(to top, rgba(0, 0, 0, 0), #000000)',
        },
      };
  }
};

const getFooterStyle = (layoutType) => {
  switch (layoutType) {
    case LAYOUT_TYPE.QUIZ:
      return {
        sticky: true,
        styles: {
          background: 'rgba(59, 59, 59, 0.3)',
        },
      };
    case LAYOUT_TYPE.DEFAULT_STICKY:
      return {
        sticky: true,
        styles: {
          backgroundImage: 'linear-gradient(to bottom, rgba(0, 0, 0, 0), #000000)',
        },
      };
    case LAYOUT_TYPE.EBOOK:
      return {
        sticky: true,
      };
    case LAYOUT_TYPE.EXTERNAL_VIDEO_PLAYER:
      return {
        sticky: true,
      };
    case LAYOUT_TYPE.DEFAULT:
    default:
      return {
        sticky: false,
        styles: {
          backgroundImage: 'linear-gradient(to bottom, rgba(0, 0, 0, 0), #000000)',
        },
      };
  }
};

const HEADER_HEIGHT = 64;
const FOOTER_HEIGHT = 91;

const FixedPanel = styled(Box)`
  width: 100%;
  position: fixed;
  z-index: 1;
`;

const Header = styled(FixedPanel)`
  min-height: ${({ theme }) => HEADER_HEIGHT + theme.space.APP_TOP_OFFSET}px;
  padding-top: ${({ theme }) => theme.space.APP_TOP_OFFSET}px;
  top: ${({ active, theme }) => (active ? 0 : `${-HEADER_HEIGHT - theme.space.APP_TOP_OFFSET}px`)};
  opacity: ${({ active }) => (active ? 1 : 0)};
  transition: top 0.3s, opacity 0.5s ease-in-out;
`;

const Footer = styled(FixedPanel)`
  min-height: ${FOOTER_HEIGHT}px;
  bottom: ${({ active }) => (active ? 0 : `${-FOOTER_HEIGHT}px`)};
  opacity: ${({ active }) => (active ? 1 : 0)};
  transition: bottom 0.3s, opacity 0.5s ease-in-out;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
`;

const FullScreenLayout = ({
  renderHeader,
  renderChildren,
  renderFooter,
  active,
  theme,
  ...restProps
}) => {
  const { layoutType } = useClassroomContext();
  const headerStyle = getHeaderStyle(layoutType);
  const footerStyle = getFooterStyle(layoutType);
  return (
    <Flex flexDirection={'column'} width={'100vw'} style={{ height: '100vh' }} {...restProps}>
      <Header active={active || headerStyle.sticky} style={{ ...headerStyle.styles }}>
        {renderHeader()}
      </Header>
      <Footer active={active || footerStyle.sticky} style={{ ...footerStyle.styles }}>
        {renderFooter()}
      </Footer>
      {renderChildren({
        active,
        headerHeight: HEADER_HEIGHT + theme.space.APP_TOP_OFFSET,
        footerHeight: FOOTER_HEIGHT,
      })}
    </Flex>
  );
};
FullScreenLayout.propTypes = {
  renderHeader: PropTypes.func,
  renderFooter: PropTypes.func,
  renderChildren: PropTypes.func,
  active: PropTypes.bool,
};
FullScreenLayout.defaultProps = {
  renderHeader: () => {},
  renderFooter: () => {},
  renderChildren: () => {},
  active: false,
};

export default compose(withTheme, withOffset)(FullScreenLayout);
